import { useState } from 'react';
import type {
  DepartmentAdminResponse,
  DepartmentsResponse,
  OrganisationAdminResponse,
  TeamAdminResponse,
  TeamsResponse,
} from '../../api/types';
import { apiPatch, apiPost, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import { ErrorState, LoadingState } from '../StateMessage';
import { ActionFeedback, AdminForm, ConfirmAction, Field, StatusTag, useAction } from './adminShared';
import styles from './admin.module.css';

/**
 * Organisation structure — the organisation, its departments and its teams
 * (Mission 018).
 *
 * Retirement here is the same act as everywhere else in this area, and the
 * confirmations say what it does *not* do. `docs/DOMAIN_MODEL.md` §11
 * records both as "Soft-retire, not deleted", and gives the reason: a
 * department is referenced by teams, and a team by the assignment history
 * that records who was in it and when. Retiring does not cascade, and the
 * counts on each row are there so nobody discovers that afterwards.
 */
export function OrganisationSection() {
  const organisation = useApiResource<OrganisationAdminResponse>('/admin/organisation');
  const [name, setName] = useState<string | null>(null);
  const { state, run } = useAction();

  if (organisation.status === 'loading') return <LoadingState label="Loading the organisation…" />;
  if (organisation.status === 'failed') {
    return <ErrorState title="The organisation could not be loaded" detail={describeError(organisation.error)} />;
  }

  const current = organisation.data;
  const value = name ?? current.name;

  async function save() {
    const ok = await run(async () => {
      await apiPatch('/admin/organisation', { name: value });
      return 'The organisation was renamed.';
    });
    if (ok) {
      setName(null);
      organisation.reload();
    }
  }

  return (
    <section className={styles.section} aria-label="Organisation">
      <h2 className={styles.sectionTitle}>Organisation</h2>
      <p className={styles.sectionNote}>
        {current.departmentCount} {current.departmentCount === 1 ? 'department' : 'departments'} ·{' '}
        {current.employeeCount} {current.employeeCount === 1 ? 'person' : 'people'}. The name is the only editable
        field, and there is no way to create or remove an organisation — this product manages one.
      </p>
      <AdminForm title="Rename the organisation" onSubmit={save} submitLabel="Save name" busy={state.status === 'saving'}>
        <Field id="organisation-name" label="Name" value={value} onChange={setName} />
        <ActionFeedback state={state} />
      </AdminForm>
    </section>
  );
}

export function DepartmentsSection() {
  const departments = useApiResource<DepartmentsResponse>('/admin/departments');
  const [creating, setCreating] = useState(false);

  return (
    <section className={styles.section} aria-label="Departments">
      <h2 className={styles.sectionTitle}>Departments</h2>
      <p className={styles.sectionNote}>
        Retiring a department does not retire the teams under it, and does not remove it — teams and roles keep
        pointing at it, and it stays listed here.
      </p>

      {departments.status === 'loading' ? <LoadingState label="Loading departments…" /> : null}
      {departments.status === 'failed' ? (
        <ErrorState title="Departments could not be loaded" detail={describeError(departments.error)} />
      ) : null}

      {departments.status === 'loaded' ? (
        <>
          <ul className={styles.list}>
            {departments.data.departments.map((department) => (
              <DepartmentRow key={department.id} department={department} onChanged={() => departments.reload()} />
            ))}
          </ul>
          {creating ? (
            <DepartmentCreator
              onDone={() => {
                setCreating(false);
                departments.reload();
              }}
            />
          ) : (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={() => setCreating(true)}>
                Add a department
              </button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function DepartmentRow({
  department,
  onChanged,
}: {
  department: DepartmentAdminResponse;
  onChanged: () => void;
}) {
  const [name, setName] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const { state, run } = useAction();
  const value = name ?? department.name;

  async function rename() {
    const ok = await run(async () => {
      await apiPatch(`/admin/departments/${department.id}`, { name: value });
      return 'The department was renamed.';
    });
    if (ok) {
      setName(null);
      setOpen(false);
      onChanged();
    }
  }

  async function retire() {
    const ok = await run(async () => {
      await apiPost(`/admin/departments/${department.id}/retirement`, {});
      return `"${department.name}" was retired.`;
    });
    if (ok) {
      setOpen(false);
      onChanged();
    }
  }

  return (
    <li className={styles.row}>
      <span className={styles.rowName}>{department.name}</span>
      <StatusTag status={department.status} />
      <span className={styles.rowMeta}>
        {department.activeTeamCount} active {department.activeTeamCount === 1 ? 'team' : 'teams'} ·{' '}
        {department.activeRoleCount} {department.activeRoleCount === 1 ? 'role' : 'roles'}
      </span>
      <span className={styles.rowActions}>
        <button type="button" className={styles.secondaryButton} onClick={() => setOpen(!open)}>
          {open ? 'Close' : 'Edit'}
        </button>
      </span>
      {open ? (
        <AdminForm
          title={`Edit ${department.name}`}
          onSubmit={rename}
          submitLabel="Save name"
          busy={state.status === 'saving'}
        >
          <Field id={`dept-name-${department.id}`} label="Name" value={value} onChange={setName} />
          <ActionFeedback state={state} />
          {department.status === 'active' ? (
            <ConfirmAction
              label="Retire this department"
              confirmLabel="Retire it"
              busy={state.status === 'saving'}
              description={
                <>
                  Retiring <strong>{department.name}</strong> marks it out of use. It is <strong>not</strong> deleted,
                  and this does <strong>not</strong> cascade: its {department.activeTeamCount} active{' '}
                  {department.activeTeamCount === 1 ? 'team stays' : 'teams stay'} active, and{' '}
                  {department.activeRoleCount} {department.activeRoleCount === 1 ? 'role keeps' : 'roles keep'}{' '}
                  pointing at it. Retire those separately if that is what you mean.
                </>
              }
              onConfirm={retire}
            />
          ) : (
            <p className={styles.locked}>This department is retired and stays listed.</p>
          )}
        </AdminForm>
      ) : null}
    </li>
  );
}

function DepartmentCreator({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const { state, run } = useAction();

  async function submit() {
    const ok = await run(async () => {
      await apiPost('/admin/departments', { name });
      return `"${name}" was added.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Add a department" onSubmit={submit} submitLabel="Create department" busy={state.status === 'saving'}>
      <Field id="new-department-name" label="Name" value={name} onChange={setName} />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}

export function TeamsSection() {
  const teams = useApiResource<TeamsResponse>('/admin/teams');
  const departments = useApiResource<DepartmentsResponse>('/admin/departments');
  const [creating, setCreating] = useState(false);

  return (
    <section className={styles.section} aria-label="Teams">
      <h2 className={styles.sectionTitle}>Teams</h2>
      <p className={styles.sectionNote}>
        A team’s lead is the one field here that is simply changed rather than recorded as history — and it is how the
        product decides who a manager’s direct reports are. Retiring a team keeps the record of who was in it.
      </p>

      {teams.status === 'loading' ? <LoadingState label="Loading teams…" /> : null}
      {teams.status === 'failed' ? (
        <ErrorState title="Teams could not be loaded" detail={describeError(teams.error)} />
      ) : null}

      {teams.status === 'loaded' ? (
        <>
          <ul className={styles.list}>
            {teams.data.teams.map((team) => (
              <TeamRow key={team.id} team={team} onChanged={() => teams.reload()} />
            ))}
          </ul>
          {creating && departments.status === 'loaded' ? (
            <TeamCreator
              departments={departments.data.departments.filter((d) => d.status === 'active')}
              onDone={() => {
                setCreating(false);
                teams.reload();
              }}
            />
          ) : (
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={() => setCreating(true)}>
                Add a team
              </button>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function TeamRow({ team, onChanged }: { team: TeamAdminResponse; onChanged: () => void }) {
  const [name, setName] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const { state, run } = useAction();
  const value = name ?? team.name;

  async function rename() {
    const ok = await run(async () => {
      await apiPatch(`/admin/teams/${team.id}`, { name: value });
      return 'The team was renamed.';
    });
    if (ok) {
      setName(null);
      setOpen(false);
      onChanged();
    }
  }

  async function retire() {
    const ok = await run(async () => {
      await apiPost(`/admin/teams/${team.id}/retirement`, {});
      return `"${team.name}" was retired.`;
    });
    if (ok) {
      setOpen(false);
      onChanged();
    }
  }

  return (
    <li className={styles.row}>
      <span className={styles.rowName}>{team.name}</span>
      <StatusTag status={team.status} />
      <span className={styles.rowMeta}>
        {team.departmentName} · {team.currentMemberCount}{' '}
        {team.currentMemberCount === 1 ? 'member' : 'members'} · led by {team.currentLeadName ?? 'nobody'}
      </span>
      <span className={styles.rowActions}>
        <button type="button" className={styles.secondaryButton} onClick={() => setOpen(!open)}>
          {open ? 'Close' : 'Edit'}
        </button>
      </span>
      {open ? (
        <AdminForm title={`Edit ${team.name}`} onSubmit={rename} submitLabel="Save name" busy={state.status === 'saving'}>
          <Field id={`team-name-${team.id}`} label="Name" value={value} onChange={setName} />
          <ActionFeedback state={state} />
          {team.status === 'active' ? (
            <ConfirmAction
              label="Retire this team"
              confirmLabel="Retire it"
              busy={state.status === 'saving'}
              description={
                <>
                  Retiring <strong>{team.name}</strong> marks it out of use. It is <strong>not</strong> deleted, its{' '}
                  {team.currentMemberCount} current {team.currentMemberCount === 1 ? 'member is' : 'members are'} not
                  unassigned, and the record of who was in this team and when stays exactly as it is.
                </>
              }
              onConfirm={retire}
            />
          ) : (
            <p className={styles.locked}>This team is retired and stays listed.</p>
          )}
        </AdminForm>
      ) : null}
    </li>
  );
}

function TeamCreator({
  departments,
  onDone,
}: {
  departments: DepartmentAdminResponse[];
  onDone: () => void;
}) {
  const [departmentId, setDepartmentId] = useState(departments[0]?.id ?? '');
  const [name, setName] = useState('');
  const { state, run } = useAction();

  async function submit() {
    const ok = await run(async () => {
      await apiPost('/admin/teams', { departmentId, name, purpose: null });
      return `"${name}" was added.`;
    });
    if (ok) onDone();
  }

  return (
    <AdminForm title="Add a team" onSubmit={submit} submitLabel="Create team" busy={state.status === 'saving'}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="new-team-department">
          Department
        </label>
        <select
          id="new-team-department"
          className={styles.input}
          value={departmentId}
          onChange={(event) => setDepartmentId(event.target.value)}
        >
          {departments.map((department) => (
            <option key={department.id} value={department.id}>
              {department.name}
            </option>
          ))}
        </select>
      </div>
      <Field id="new-team-name" label="Name" value={name} onChange={setName} />
      <ActionFeedback state={state} />
    </AdminForm>
  );
}
